import { createContext, useCallback, useContext, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const DoxaConfirmContext = createContext(null);

const CONFIRM_VARIANTS = {
  default: "bg-[#F75A1B] text-white hover:bg-[#D94A12] focus-visible:ring-[#F75A1B]/30",
  positive: "bg-[#2F7A45] text-white hover:bg-[#28683B] focus-visible:ring-[#2F7A45]/30",
  warning: "bg-[#8A5A16] text-white hover:bg-[#744B12] focus-visible:ring-[#8A5A16]/30",
  destructive: "bg-[#B42318] text-white hover:bg-[#912018] focus-visible:ring-[#B42318]/30",
};

export function DoxaConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  cancelLabel = "Cancelar",
  confirmLabel = "Confirmar",
  loadingLabel,
  variant = "default",
  loading = false,
  onConfirm,
  dataTestId = "doxa-confirm-dialog",
}) {
  const handleOpenChange = (nextOpen) => {
    if (loading && !nextOpen) return;
    onOpenChange?.(nextOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent
        className="w-[calc(100%-2rem)] max-w-md gap-5 rounded-[20px] border-[#E0D9CD] bg-white p-5 shadow-[0_24px_70px_rgba(45,31,20,.20)] sm:p-6"
        data-testid={dataTestId}
      >
        <AlertDialogHeader className="space-y-2 text-left">
          <AlertDialogTitle className="font-display text-xl font-bold leading-tight text-[#1F1F1F]">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm leading-relaxed text-[#6B655E]">{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="gap-2 sm:space-x-0">
          <AlertDialogCancel
            disabled={loading}
            className="mt-0 w-full rounded-xl border-[#E0D9CD] bg-white px-4 py-2.5 text-sm font-semibold text-[#4A4640] hover:bg-[#FAF7F1] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            data-testid={`${dataTestId}-cancel`}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={cn(
              "inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto",
              CONFIRM_VARIANTS[variant] || CONFIRM_VARIANTS.default,
            )}
            data-testid={`${dataTestId}-confirm`}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            <span aria-live="polite">{loading ? (loadingLabel || `${confirmLabel}…`) : confirmLabel}</span>
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function DoxaConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const activeRequestRef = useRef(null);
  const submittingRef = useRef(false);

  const confirm = useCallback((config) => {
    if (activeRequestRef.current) return Promise.resolve(false);

    return new Promise((resolve) => {
      const nextRequest = { ...config, resolve };
      activeRequestRef.current = nextRequest;
      setRequest(nextRequest);
    });
  }, []);

  const finish = (result) => {
    const current = activeRequestRef.current;
    activeRequestRef.current = null;
    setRequest(null);
    current?.resolve?.(result);
  };

  const handleOpenChange = (nextOpen) => {
    if (nextOpen || submittingRef.current || loading) return;
    finish(false);
  };

  const handleConfirm = async () => {
    const current = activeRequestRef.current;
    if (!current || submittingRef.current) return;

    submittingRef.current = true;
    setLoading(true);
    try {
      await current.onConfirm?.();
      finish(true);
    } catch (error) {
      current.onError?.(error);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <DoxaConfirmContext.Provider value={confirm}>
      {children}
      <DoxaConfirmDialog
        open={Boolean(request)}
        onOpenChange={handleOpenChange}
        title={request?.title}
        description={request?.description}
        cancelLabel={request?.cancelLabel}
        confirmLabel={request?.confirmLabel}
        loadingLabel={request?.loadingLabel}
        variant={request?.variant}
        loading={loading}
        onConfirm={handleConfirm}
        dataTestId={request?.dataTestId}
      />
    </DoxaConfirmContext.Provider>
  );
}

export function useDoxaConfirm() {
  const confirm = useContext(DoxaConfirmContext);
  if (!confirm) throw new Error("useDoxaConfirm must be used within DoxaConfirmProvider");
  return confirm;
}
